import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { Field } from '../components/Field';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { CompteBetaAvis } from '../components/CompteBetaAvis';

/** Ce que l'API rend avant la suppression (cf. `SuppressionCompteService.apercu`). */
interface ApercuSuppression {
  email: string;
  possible: boolean;
  blocage: { code: 'PROPRIETE_A_TRANSFERER' | 'COMPTE_ADMINISTRATEUR'; message: string } | null;
  espace: {
    nom: string;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
    autresMembres: number;
    issue: 'QUITTER' | 'FERMER' | 'TRANSFERT_REQUIS';
  } | null;
  abonnements: { produit: string; fin: string; store?: 'APPLE' | 'GOOGLE' | null }[];
  reconnexionRequise: boolean;
  /** Délai avant effacement, compté à partir de la mise en pause (jours). */
  delaiJours: number;
  /** Si la demande était faite maintenant : mise en pause (fin de la période payée), puis suppression. */
  pauseLe: string;
  suppressionLe: string;
  /** Demande déjà en cours (03/11/2026), ou `null`. */
  programmee: { pauseLe: string; suppressionLe: string; enPause: boolean } | null;
}

/** Réponse à l'annulation (cf. `SuppressionCompteService.annuler`). */
interface ResultatAnnulation {
  annulee: boolean;
  abonnementsRepris: number;
  aReactiver: { produit: string }[];
}

const CONFIRMATION = 'SUPPRIMER';
const ICI = '/compte/suppression';

/** « 1er novembre 2026 », et non « 1 novembre 2026 ». */
const date = (iso: string) =>
  new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(iso))
    .replace(/^1 /, '1er ');

/**
 * Supprimer son compte SEmply depuis le portail (01/10/2026).
 *
 * Google Play exige que le compte puisse être supprimé SANS l'appli : la page
 * publique semply.fr/suppression-compte renvoie ici. Mêmes règles que dans
 * l'appli — connexion de moins de dix minutes, propriété d'un espace partagé à
 * transférer d'abord —, appliquées par l'API ; cette page montre ce qui va se
 * passer, puis demande de taper SUPPRIMER.
 *
 * Règles du 03/11/2026 :
 *  - l'espace reste ACTIF jusqu'à la fin de la période déjà payée (aucun
 *    remboursement) ; les abonnements ne sont pas renouvelés ;
 *  - il passe ensuite EN PAUSE, et les données sont effacées trente jours
 *    plus tard (sauf les factures de vente, conservées dix ans) ;
 *  - aucune déconnexion : la page montre la demande en cours et propose
 *    « Annuler la suppression », en demandant s'il faut réactiver les
 *    abonnements.
 */
export function SuppressionComptePage() {
  const navigate = useNavigate();
  const { user, loading, fetchMe, logout } = useAuthStore();
  const [apercu, setApercu] = useState<ApercuSuppression | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  /** Compte effacé sur-le-champ (aucun espace à garder actif) : plus de session. */
  const [supprime, setSupprime] = useState<string | null>(null);
  /** Rechargement de l'aperçu après une demande ou une annulation. */
  const [version, setVersion] = useState(0);
  /** Annulation : question « réactiver les abonnements ? » ouverte. */
  const [question, setQuestion] = useState(false);
  const [annulation, setAnnulation] = useState<ResultatAnnulation | null>(null);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (loading || supprime) return;
    // Pas de session : connexion, puis retour ICI (liste fermée du portail).
    if (!user) {
      navigate(`/login?${new URLSearchParams({ suite: ICI })}`);
      return;
    }
    let vivant = true;
    api
      .get<ApercuSuppression>('/auth/suppression')
      .then((a) => {
        if (vivant) setApercu(a);
      })
      .catch((err: unknown) => {
        if (vivant) setErreur(err instanceof ApiError ? err.message : 'Portail injoignable.');
      });
    return () => {
      vivant = false;
    };
  }, [loading, user, supprime, navigate, version]);

  /** Reconnexion : on ferme la session, et la connexion ramène ici. */
  async function seReconnecter() {
    await logout();
    navigate(`/login?${new URLSearchParams({ suite: ICI })}`);
  }

  async function supprimer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saisie.trim() !== CONFIRMATION || envoi || !apercu) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const resultat = await api.post<{ suppressionLe?: string | null }>('/auth/suppression', {
        confirmation: CONFIRMATION,
      });
      setSaisie('');
      setAnnulation(null);
      if (resultat.suppressionLe === null) {
        // Effacement immédiat : la session n'existe plus.
        setSupprime(apercu.email);
        return;
      }
      // La session reste ouverte : on recharge l'aperçu, qui montre la demande en cours.
      setVersion((v) => v + 1);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'server_error';
      if (code === 'REAUTHENTIFICATION_REQUISE') {
        setApercu({ ...apercu, reconnexionRequise: true });
        return;
      }
      setErreur(
        err instanceof ApiError ? err.message : 'La suppression n’a pas abouti. Réessayez.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  async function annuler(reprendreAbonnements: boolean) {
    if (envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const resultat = await api.post<ResultatAnnulation>('/auth/suppression/annuler', {
        reprendreAbonnements,
      });
      setAnnulation(resultat);
      setQuestion(false);
      setVersion((v) => v + 1);
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'L’annulation n’a pas abouti. Réessayez.');
    } finally {
      setEnvoi(false);
    }
  }

  if (supprime) {
    return (
      <AuthCard title="Compte supprimé" subtitle={supprime}>
        <p>Votre compte SEmply a été supprimé.</p>
        <p>
          <a href="https://semply.fr">Retour sur semply.fr</a>
        </p>
      </AuthCard>
    );
  }

  if (loading || !user || (!apercu && !erreur)) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  if (!apercu) {
    return (
      <AuthCard title="Supprimer mon compte">
        <Alert message={erreur} />
        <div className="links">
          <Link to="/compte">Retour à mon compte</Link>
        </div>
      </AuthCard>
    );
  }

  const espace = apercu.espace;
  const programmee = apercu.programmee;

  if (programmee) {
    return (
      <AuthCard title="Suppression programmée" subtitle={apercu.email}>
        <Alert message={erreur} />
        {programmee.enPause ? (
          <p>
            Votre espace est <strong>en pause</strong> depuis le {date(programmee.pauseLe)} : il
            n’est plus utilisable. Vos données seront supprimées le{' '}
            <strong>{date(programmee.suppressionLe)}</strong>, sauf les factures de vente,
            conservées dix ans comme l’exige la loi.
          </p>
        ) : (
          <p>
            Votre espace reste <strong>actif jusqu’au {date(programmee.pauseLe)}</strong>, fin de la
            période déjà payée. Il passera ensuite en pause, et vos données seront supprimées le{' '}
            <strong>{date(programmee.suppressionLe)}</strong> (sauf les factures de vente,
            conservées dix ans).
          </p>
        )}
        <p>Vous avez changé d’avis ? Vous pouvez annuler la suppression jusqu’à cette date.</p>

        {question ? (
          <>
            <p>
              <strong>Souhaitez-vous réactiver vos abonnements ?</strong> Ils reprendront leur
              renouvellement normal ; ceux déjà terminés devront être souscrits de nouveau.
            </p>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <button
                className="btn"
                type="button"
                disabled={envoi}
                onClick={() => void annuler(true)}
              >
                {envoi ? 'Envoi…' : 'Oui, annuler et réactiver mes abonnements'}
              </button>
              <button
                className="btn"
                type="button"
                disabled={envoi}
                onClick={() => void annuler(false)}
              >
                Non, annuler sans réactiver
              </button>
            </div>
            <div className="links">
              <button type="button" className="link-button" onClick={() => setQuestion(false)}>
                Retour
              </button>
            </div>
          </>
        ) : (
          <button className="btn" type="button" onClick={() => setQuestion(true)}>
            Annuler la suppression
          </button>
        )}

        <div className="links">
          <Link to="/compte">Retour à mon compte</Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Supprimer mon compte" subtitle={apercu.email}>
      <Alert message={erreur} />

      {annulation ? (
        <>
          <Alert
            variant="success"
            message={
              'La suppression est annulée.' +
              (annulation.abonnementsRepris > 0
                ? ` ${annulation.abonnementsRepris} abonnement(s) réactivé(s).`
                : '') +
              (annulation.aReactiver.length > 0
                ? ` À souscrire de nouveau : ${annulation.aReactiver.map((a) => a.produit).join(', ')}.`
                : '')
            }
          />
          {annulation.aReactiver.length > 0 ? (
            <div className="links">
              <Link to="/abonnements">Souscrire de nouveau dans Mes abonnements</Link>
            </div>
          ) : null}
        </>
      ) : null}

      <p>
        Votre espace reste <strong>actif jusqu’au {date(apercu.pauseLe)}</strong>, fin de la période
        déjà payée (aucun remboursement n’est effectué). Il passe ensuite en pause, et votre compte
        est supprimé définitivement {apercu.delaiJours} jours plus tard, le{' '}
        <strong>{date(apercu.suppressionLe)}</strong>, sur toute la suite SEmply. Vous restez
        connecté, et pouvez annuler depuis cette page jusqu’à cette date.
      </p>
      <ul
        style={{
          fontSize: '0.9rem',
          lineHeight: 1.6,
          paddingLeft: '1.2rem',
          // Puces retirées par la charte (`li { list-style: none }`) : l'écart
          // sépare les points.
          display: 'grid',
          gap: '0.5rem',
        }}
      >
        <li>
          Effacés à cette date : votre nom, votre adresse e-mail, votre mot de passe, vos connexions
          Google, Apple ou Microsoft et votre double authentification.
        </li>
        {espace?.issue === 'QUITTER' ? (
          <li>Vous quitterez l’espace « {espace.nom} », qui continuera sans vous.</li>
        ) : null}
        {espace?.issue === 'FERMER' ? (
          <li>
            L’espace « {espace.nom} » sera fermé à cette date et les données de ses dossiers
            effacées, sauf les factures de vente et pièces comptables, conservées dix ans comme
            l’exige la loi.
          </li>
        ) : null}
        {apercu.abonnements.length > 0 ? (
          <li>
            Abonnements résiliés dès votre demande, sans renouvellement :{' '}
            {apercu.abonnements.map((a) => `${a.produit} (jusqu’au ${date(a.fin)})`).join(', ')}. Si
            vous annulez, vous pourrez choisir de les réactiver.
          </li>
        ) : null}
        {apercu.abonnements.some((a) => a.store) ? (
          <li>
            Un abonnement pris dans l’appli mobile doit être résilié vous-même sur l’App Store ou
            Google Play : SEmply ne peut pas l’arrêter à votre place.
          </li>
        ) : null}
      </ul>

      {user.betaCompte ? (
        // Compte bêta partagé (03/10/2026) : la suppression est refusée par l'API.
        <CompteBetaAvis />
      ) : apercu.blocage ? (
        <>
          <Alert message={apercu.blocage.message} />
          {apercu.blocage.code === 'PROPRIETE_A_TRANSFERER' ? (
            <div className="links">
              <Link to="/espace">Désigner un nouveau propriétaire</Link>
            </div>
          ) : null}
        </>
      ) : apercu.reconnexionRequise ? (
        <>
          <p>
            Pour confirmer qu’il s’agit bien de vous, reconnectez-vous : vous reviendrez sur cette
            page, et aurez alors dix minutes pour confirmer la suppression.
          </p>
          <button className="btn" type="button" onClick={() => void seReconnecter()}>
            Me reconnecter
          </button>
        </>
      ) : (
        <form onSubmit={(e) => void supprimer(e)}>
          <Field
            label={`Tapez ${CONFIRMATION} pour confirmer`}
            name="confirmation"
            autoComplete="off"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value.toUpperCase())}
          />
          <button
            className="btn btn-danger"
            type="submit"
            disabled={envoi || saisie.trim() !== CONFIRMATION}
          >
            {envoi ? 'Envoi…' : 'Programmer la suppression de mon compte'}
          </button>
        </form>
      )}

      <div className="links">
        <Link to="/compte">Annuler</Link>
      </div>
    </AuthCard>
  );
}
